/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { z } from 'zod';

import { IAddressAddItem, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address';
import { createBaseFieldSchemas, createTranslationContext } from '../../../validation';
import { getAddressSchema } from '../../../validation/zod-schemas/address';
import { IAddressList, IAddressListItem, IAddressListProps } from '@msdyn365-commerce-modules/address';
import { IAddressShowItem, IAddressShowProps } from '@msdyn365-commerce-modules/address';
import { IAccountManagementAddressViewProps } from '@msdyn365-commerce-modules/address';
import { convert_a2_to_a3 } from '../utils/convert_a2_a3';
import { cleanAddress } from '../../../common/utils/address';
import { PhoneCountryCodeInput } from '../../../common/components/phone-number-input';
// @ts-ignore - JSON import
import countryCodeOptions from '../../../common/components/phone-number-input/countryCodes.json';
import { useGlobalLoader } from '../../../common/components/global-loader';
import { CountryOption } from '../../../common/components/phone-number-input/types';

interface ExtendedAddressAddUpdateProps extends IAddressAddUpdateProps {
    moduleProps?: {
        resources?: any;
        config?: any;
        context?: {
            request?: {
                locale?: string;
                sitePath?: string;
                channel?: {
                    Currency?: string;
                };
            };
        };
    };
}

interface CountryConfig {
    autocomplete: string[];
    select: string[];
    exclude?: Array<{
        type: string;
        short_name: string;
        long_name: string;
    }>;
}

interface CountriesAvailable {
    [key: string]: CountryConfig;
}

interface CustomAlertProps {
    message: string;
    isVisible: boolean;
    className?: string;
}

const BASE_COUNTRY_CONFIGS: { [key: string]: CountryConfig } = {
    GB: {
        autocomplete: ['gb'],
        select: ['GBR'],
        exclude: [
            { type: 'administrative_area_level_1', short_name: 'NIR', long_name: 'Northern Ireland' },
            { type: 'administrative_area_level_1', short_name: 'Northern Ireland', long_name: 'Northern Ireland' },
            { type: 'postal_code', short_name: 'BT', long_name: 'BT' }
        ]
    },
    IT: {
        autocomplete: ['it'],
        select: ['ITA'],
        exclude: []
    },
    US: {
        autocomplete: ['us'],
        select: ['USA'],
        exclude: []
    },
    EU: {
        autocomplete: [],
        select: [
            'AUT',
            'BEL',
            'BGR',
            'CHE',
            'CZE',
            'DEU',
            'DNK',
            'ESP',
            'EST',
            'FIN',
            'FRA',
            'GRC',
            'HRV',
            'HUN',
            'IRL',
            'LIE',
            'LTU',
            'LUX',
            'LVA',
            'MCO',
            'NLD',
            'NOR',
            'POL',
            'PRT',
            'ROU',
            'SVK',
            'SVN',
            'SWE'
        ],
        exclude: []
    },
    SG: {
        autocomplete: ['sg'],
        select: ['SGP'],
        exclude: []
    },
    HK: {
        autocomplete: ['hk'],
        select: ['HKG'],
        exclude: []
    }
};

const LOCALE_TO_COUNTRY: { [key: string]: string } = {
    it: 'IT',
    enit: 'IT',
    enus: 'US',
    us: 'US',
    engb: 'GB',
    gb: 'GB',
    fr: 'FR',
    enfr: 'FR',
    de: 'DE',
    ende: 'DE',
    au: 'AU',
    enau: 'AU',
    sg: 'SG',
    ensg: 'SG',
    hk: 'HK',
    enhk: 'HK',
    zhhk: 'HK'
};

const normalizeLocale = (locale: string): string => locale.toLowerCase().replace('-', '');

const getCountryCodeFromLocale = (locale: string): string => {
    const normalizedLocale = normalizeLocale(locale);
    return LOCALE_TO_COUNTRY[normalizedLocale] || 'US';
};

const isUSLocale = (locale: string): boolean => {
    const normalizedLocale = normalizeLocale(locale);
    return ['enus', 'us'].includes(normalizedLocale);
};

const isSGLocale = (locale: string): boolean => {
    const normalizedLocale = normalizeLocale(locale);
    return ['ensg', 'sg'].includes(normalizedLocale);
};

const isITLocale = (locale: string): boolean => {
    const normalizedLocale = normalizeLocale(locale);
    return ['it', 'enit'].includes(normalizedLocale);
};

const generateGoogleMapsCountriesAvail = (): CountriesAvailable => {
    const result: CountriesAvailable = {};

    Object.entries(LOCALE_TO_COUNTRY).forEach(([locale, countryKey]) => {
        const config = BASE_COUNTRY_CONFIGS[countryKey];
        if (config) {
            result[locale] = config;
            result[locale.toLowerCase()] = config;
            result[locale.toUpperCase()] = config;
        }
    });

    const commonMixedCaseLocales = {
        enUS: 'US',
        enSG: 'SG',
        enHK: 'HK',
        enIT: 'IT',
        enGB: 'GB',
        enFR: 'FR',
        enDE: 'DE',
        zhHK: 'HK'
    };

    Object.entries(commonMixedCaseLocales).forEach(([locale, countryKey]) => {
        const config = BASE_COUNTRY_CONFIGS[countryKey];
        if (config) {
            result[locale] = config;
        }
    });

    result['eu'] = BASE_COUNTRY_CONFIGS.EU;

    return result;
};

const CustomAlert: React.FC<CustomAlertProps> = ({ message, isVisible, className = 'msc-address-form__alert' }) => {
    if (!isVisible || !message) {
        return null;
    }

    return (
        <div className={className}>
            <span>{message}</span>
        </div>
    );
};

const AddressShow: React.FC<IAddressShowProps> = ({ AddressDetail, items }) => {
    return (
        <Node {...AddressDetail}>
            {items &&
                items.map((item: IAddressShowItem) => {
                    return <>{cleanAddress(item.description)}</>;
                })}
        </Node>
    );
};

const AddressList: React.FC<IAddressList> = ({ List, isShowList, heading, items }) => {
    if (!isShowList) {
        return null;
    }

    return (
        <Node {...List}>
            {heading}
            {items &&
                items.map((item: IAddressListItem) => {
                    const { Item, key, error, showItems, isShowPrimaryButton, primaryButton, editButton, removeButton } = item;
                    const { Error, isShowError, title, message } = error;
                    return (
                        <Node {...Item} key={key}>
                            <AddressShow {...showItems} />
                            {isShowError && (
                                <Node {...Error}>
                                    {title}
                                    {message}
                                </Node>
                            )}
                            {isShowPrimaryButton && primaryButton}
                            {editButton}
                            {removeButton}
                        </Node>
                    );
                })}
        </Node>
    );
};

const AddressLists: React.FC<IAddressListProps> = ({
    ListAddress,
    heading,
    isShowEmptyListMessage,
    emptyListMessage,
    addButton,
    primaryAddressList,
    otherAddressList
}) => {
    return (
        <Node {...ListAddress}>
            {heading}
            {isShowEmptyListMessage && emptyListMessage}
            {addButton}
            <AddressList {...primaryAddressList} />
            <AddressList {...otherAddressList} />
        </Node>
    );
};
const setupGoogleMapsAutocomplete = (locale: string, resources: any, countriesAvail: any) => {
    if (typeof window === 'undefined' || !(window as any).google) {
        return;
    }

    const autocompleteFormField = document.getElementById('shipping_addressstreet');
    if (!autocompleteFormField) {
        return;
    }

    function isExcludedPlace(addressComponents: any[], excludeArr: any[] = []) {
        if (!excludeArr.length) {
            return false;
        }
        for (const ex of excludeArr) {
            for (const comp of addressComponents) {
                if (
                    comp.types.includes(ex.type) &&
                    (comp.short_name.toLowerCase().includes(ex.short_name.toLowerCase()) ||
                        comp.long_name.toLowerCase().includes(ex.long_name.toLowerCase()))
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    const autocomplete = new (window as any).google.maps.places.Autocomplete(autocompleteFormField, {
        types: ['address'],
        componentRestrictions: countriesAvail[locale]?.autocomplete?.length > 0 ? { country: countriesAvail[locale].autocomplete } : {}
    });

    autocompleteFormField.setAttribute('placeholder', resources?.startTypingYourAddress || 'Start typing your address');

    (window as any).google.maps.event.clearInstanceListeners(autocompleteFormField);

    (window as any).google.maps.event.addListener(autocomplete, 'place_changed', () => {
        const place = autocomplete.getPlace();
        const excludeArr = countriesAvail[locale]?.exclude || [];

        if (place.address_components && isExcludedPlace(place.address_components, excludeArr)) {
            const streetContainer = autocompleteFormField.closest('.msc-address-form__item');

            streetContainer?.classList.add('address-form__item-invalid');
            return;
        }

        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;

        if (place.name && nativeInputValueSetter) {
            nativeInputValueSetter.call(autocompleteFormField, place.name);
            const event = new Event('change', { bubbles: true });
            autocompleteFormField.dispatchEvent(event);
        }

        const googleComponents = [
            { googleComponent: 'sublocality_level_1', id: 'shipping_addresscity' },
            { googleComponent: 'locality', id: 'shipping_addresscity' },
            { googleComponent: 'postal_town', id: 'shipping_addresscity' },
            { googleComponent: 'administrative_area_level_3', id: 'shipping_addresscity' },
            { googleComponent: 'administrative_area_level_1', id: 'shipping_addressstate' },
            { googleComponent: 'postal_code_prefix', id: 'shipping_addresszipcode' },
            { googleComponent: 'postal_code', id: 'shipping_addresszipcode' },
            { googleComponent: 'country', id: 'shipping_addressthreeletterisoregionname' },
            { googleComponent: 'administrative_area_level_2', id: 'shipping_addresscounty' }
        ];

        if (place.address_components) {
            place.address_components.forEach((component: any) => {
                const addressType = component.types[0];
                const matchingField = googleComponents.find(gc => gc.googleComponent === addressType);

                if (matchingField) {
                    if (matchingField.id === 'shipping_addresscounty' && !isITLocale(locale)) {
                        return;
                    }

                    const element = document.getElementById(matchingField.id);
                    if (element) {
                        let value = component.long_name;

                        if (matchingField.id === 'shipping_addressthreeletterisoregionname') {
                            value = (convert_a2_to_a3 as any)[component.short_name] || component.short_name;
                        } else if (matchingField.id === 'shipping_addressstate' && locale === 'it') {
                            value = 'DEF';
                        } else if (matchingField.id === 'shipping_addressstate' && isUSLocale(locale)) {
                            value = component.short_name;
                        } else if (matchingField.id === 'shipping_addresscounty') {
                            value = component.short_name;
                        }

                        if (element.tagName === 'SELECT') {
                            (element as HTMLSelectElement).value = value;
                            const event = new Event('change', { bubbles: true });
                            element.dispatchEvent(event);
                        } else if (nativeInputValueSetter) {
                            nativeInputValueSetter.call(element, value);
                            const event = new Event('input', { bubbles: true });
                            element.dispatchEvent(event);
                        }
                    }
                }
            });
        }
    });
};

const setupFieldValidation = (locale: string, resources: any, localizedSchemas: any, setFieldError: any, clearFieldError: any) => {
    const cleanupFunctions: (() => void)[] = [];

    const validateSingleField = (fieldKey: string, value: string, countryCode?: string) => {
        try {
            const { addressSchemas, postalCodeWithCountrySchema, countryCodeSchema } = localizedSchemas;

            switch (fieldKey) {
                case 'Name':
                    addressSchemas.name.parse(value);
                    break;
                case 'Street':
                    addressSchemas.street.parse(value);
                    break;
                case 'City':
                    addressSchemas.city.parse(value);
                    break;
                case 'ZipCode':
                    if (countryCode) {
                        postalCodeWithCountrySchema(countryCode).parse(value);
                    } else {
                        addressSchemas.zipCode.parse(value);
                    }
                    break;
                case 'State':
                    if (isUSLocale(locale)) {
                        addressSchemas.state.parse(value);
                    } else if (value.trim()) {
                        addressSchemas.state.optional().parse(value);
                    }
                    break;
                case 'CountyName':
                case 'County':
                    if (isITLocale(locale)) {
                        addressSchemas.county
                            .min(2, 'County code must be 2 characters')
                            .max(2, 'County code must be 2 characters')
                            .regex(/^[A-Z]{2}$/, 'County code must be uppercase letters')
                            .parse(value.toUpperCase());
                    } else if (value.trim()) {
                        addressSchemas.county.optional().parse(value);
                    }
                    break;
                case 'ThreeLetterISORegionName':
                    countryCodeSchema.parse(value);
                    break;
                default:
                    return { isValid: true, message: '' };
            }
            return { isValid: true, message: '' };
        } catch (error) {
            if (error instanceof z.ZodError) {
                return { isValid: false, message: error.errors[0]?.message || 'Invalid value' };
            }
            return { isValid: false, message: 'Validation error' };
        }
    };

    const countryCode = getCountryCodeFromLocale(locale);

    const fieldMappings = [
        { id: 'shipping_addressname', key: 'Name', required: true },
        { id: 'shipping_addressstreet', key: 'Street', required: true },
        { id: 'shipping_addresscity', key: 'City', required: true },
        { id: 'shipping_addresszipcode', key: 'ZipCode', required: true },
        { id: 'shipping_addresscounty', key: 'CountyName', required: isITLocale(locale) }
        // Phone field is now handled by PhoneCountryCodeInput component
    ];

    fieldMappings.forEach(mapping => {
        const field = document.getElementById(mapping.id) as HTMLInputElement;
        if (!field) return;

        if (mapping.key === 'Name') {
            field.setAttribute('maxlength', '34');
        }

        // Set maxlength for CountyName field in Italian locale
        if (mapping.key === 'CountyName' && isITLocale(locale)) {
            field.setAttribute('maxlength', '2');
        }

        const clearError = () => {
            const currentContainer = field.closest('.msc-address-form__item') as HTMLElement;
            if (currentContainer) {
                currentContainer.classList.remove('address-form__item-invalid');
            }
            clearFieldError(mapping.key);
        };

        const validateField = () => {
            let value = field.value.trim();
            const currentContainer = field.closest('.msc-address-form__item') as HTMLElement;

            // Apply transform for CountyName in Italian locale
            if (mapping.key === 'CountyName' && isITLocale(locale)) {
                field.value = value.toUpperCase();
            }

            if (currentContainer) {
                currentContainer.classList.remove('address-form__item-invalid');
            }

            clearFieldError(mapping.key);

            if (!value.length && !mapping.required) {
                return true;
            }

            const validation = validateSingleField(mapping.key, value, countryCode);
            if (!validation.isValid) {
                if (currentContainer) {
                    currentContainer.classList.add('address-form__item-invalid');
                }
                setFieldError(mapping.key, validation.message);
                return false;
            }

            return true;
        };

        field.addEventListener('input', clearError);
        field.addEventListener('blur', validateField);
        cleanupFunctions.push(() => {
            field.removeEventListener('input', clearError);
            field.removeEventListener('blur', validateField);
        });
    });

    const selectMappings = [
        { id: 'shipping_addressstate', key: 'State', required: isUSLocale(locale) },
        { id: 'shipping_addressthreeletterisoregionname', key: 'ThreeLetterISORegionName', required: true }
    ];

    selectMappings.forEach(mapping => {
        const select = document.getElementById(mapping.id) as HTMLSelectElement;
        if (!select) return;

        const clearError = () => {
            const currentContainer = select.closest('.msc-address-form__item') as HTMLElement;
            if (currentContainer) {
                currentContainer.classList.remove('address-form__item-invalid');
            }
            clearFieldError(mapping.key);
        };

        const validateSelect = () => {
            const value = select.value;
            const currentContainer = select.closest('.msc-address-form__item') as HTMLElement;

            if (currentContainer) {
                currentContainer.classList.remove('address-form__item-invalid');
            }

            clearFieldError(mapping.key);

            if (!value.length && !mapping.required) {
                return true;
            }

            const validation = validateSingleField(mapping.key, value, countryCode);
            if (!validation.isValid) {
                if (currentContainer) {
                    currentContainer.classList.add('address-form__item-invalid');
                }
                setFieldError(mapping.key, validation.message);
                return false;
            }

            return true;
        };

        select.addEventListener('change', clearError);
        select.addEventListener('blur', validateSelect);
        cleanupFunctions.push(() => {
            select.removeEventListener('change', clearError);
            select.removeEventListener('blur', validateSelect);
        });
    });

    return () => {
        cleanupFunctions.forEach(cleanup => cleanup());
    };
};

const AddressAddUpdate: React.FC<ExtendedAddressAddUpdateProps> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton,
    moduleProps,
    ...other
}) => {
    const { showLoader } = useGlobalLoader();
    const resources = moduleProps?.resources || (other as any)?.moduleProps?.resources;
    const localeString = moduleProps?.context?.request?.locale || (other as any)?.moduleProps?.context?.request?.locale;
    const sitePath = moduleProps?.context?.request?.sitePath || (other as any)?.moduleProps?.context?.request?.sitePath;
    const currency = moduleProps?.context?.request?.channel?.Currency || (other as any)?.moduleProps?.context?.request?.channel?.Currency;
    const config = moduleProps?.config || (other as any)?.moduleProps?.config;
    const europe = ['/fr', '/de'];

    const locale = React.useMemo(() => {
        if (sitePath && europe.includes(sitePath)) {
            return 'eu';
        } else if (currency === 'EUR' && localeString === 'en-GB') {
            return 'eu';
        } else if (localeString) {
            // Normalize locale string: en-US -> enUS, en-SG -> enSG, etc.
            return localeString.replace('-', '');
        } else {
            return 'en';
        }
    }, [sitePath, localeString, currency]);

    const countriesAvail = React.useMemo(() => generateGoogleMapsCountriesAvail(), []);

    const addressSchema = React.useMemo(() => getAddressSchema(locale), [locale]);

    const [localizedSchemas, setLocalizedSchemas] = React.useState(() => createBaseFieldSchemas());

    const [validationErrors, setValidationErrors] = React.useState<{ [key: string]: string }>({});

    // Phone field state management
    // Prefill from existing items if we are editing an address
    const computeInitialPhone = (): { full?: string; initialDial?: string } => {
        try {
            const phoneItem = (items || []).find((it: any) => it && it.key === 'Phone');
            const inputNode = phoneItem?.input as any;
            const candidate: string = inputNode?.props?.value ?? inputNode?.props?.defaultValue ?? '';
            if (typeof candidate === 'string' && candidate.trim()) {
                const valueSanitized = candidate.replace(/\s+/g, '');

                // Sort countries by dial_code length desc to prefer longest match
                const sortedCountries = [...(countryCodeOptions as any[])].sort(
                    (a: any, b: any) => b.dial_code.replace(/\s+/g, '').length - a.dial_code.replace(/\s+/g, '').length
                );

                for (const country of sortedCountries) {
                    const countryDialSanitized = (country.dial_code || '').replace(/\s+/g, '');
                    if (countryDialSanitized && valueSanitized.startsWith(countryDialSanitized)) {
                        return { full: candidate.trim(), initialDial: country.dial_code };
                    }
                }

                return { full: candidate.trim() };
            }
        } catch (e) {
            // ignore and default to empty
        }
        return {};
    };

    const initialPhone = computeInitialPhone();
    const [fullPhoneNumber, setFullPhoneNumber] = React.useState<string>(initialPhone.full || '');
    const initialCountryDialCode = initialPhone.initialDial || '';

    // Get country code for phone component
    const phoneCountryCode = React.useMemo(() => {
        return getCountryCodeFromLocale(locale);
    }, [locale]);

    const setFieldError = React.useCallback((fieldKey: string, message: string) => {
        setValidationErrors(prev => ({
            ...prev,
            [fieldKey]: message
        }));
    }, []);

    const clearFieldError = React.useCallback((fieldKey: string) => {
        setValidationErrors(prev => {
            const newState = { ...prev };
            delete newState[fieldKey];
            return newState;
        });
    }, []);

    // Handle phone number change callback
    const handlePhoneNumberChange = React.useCallback(
        (phoneNum: string, fullPhone: string, hasValidationError: boolean, selectedCountry?: CountryOption) => {
            setFullPhoneNumber(fullPhone);

            // Update hidden field for form submission
            const hiddenField = document.getElementById('shipping_addressphone_full') as HTMLInputElement;
            if (hiddenField) {
                hiddenField.value = fullPhone;
                hiddenField.setAttribute('country-id', selectedCountry?.id || selectedCountry?.code || '');
            }

            // Clear or set field error based on validation
            if (!hasValidationError) {
                clearFieldError('Phone');
            } else {
                setFieldError('Phone', resources?.phoneValidationErrorRequired || 'Phone number is invalid');
            }
        },
        [setFieldError, clearFieldError, resources]
    );

    React.useEffect(() => {
        const loadTranslationContext = async () => {
            try {
                const hasCustomKeys = resources && resources.phoneValidationErrorRequired;

                const context = await createTranslationContext(locale, hasCustomKeys ? resources : undefined);
                const schemas = createBaseFieldSchemas(context);
                setLocalizedSchemas(schemas);
            } catch (error) {
                console.warn('Failed to create translation context:', error);
                const fallbackSchemas = createBaseFieldSchemas();
                setLocalizedSchemas(fallbackSchemas);
            }
        };

        if (resources) {
            loadTranslationContext();
        } else {
            setLocalizedSchemas(createBaseFieldSchemas());
        }
    }, [locale, resources]);

    React.useEffect(() => {
        const cleanup = setupFieldValidation(locale, resources, localizedSchemas, setFieldError, clearFieldError);
        return cleanup;
    }, [locale, resources, localizedSchemas, setFieldError, clearFieldError]);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setupGoogleMapsAutocomplete(locale, resources, countriesAvail);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    React.useEffect(() => {
        const options = document.querySelectorAll('#shipping_addressthreeletterisoregionname option');

        if (countriesAvail[locale] && countriesAvail[locale].select.length > 0) {
            options.forEach(o => {
                const optionV = (o as HTMLOptionElement).value;
                if (!countriesAvail[locale].select.includes(optionV)) {
                    o.remove();
                }
            });
        }

        const countrySelect = document.getElementById('shipping_addressthreeletterisoregionname') as HTMLSelectElement;
        if (isUSLocale(locale) && countrySelect) {
            countrySelect.value = 'USA';
            const event = new Event('change', { bubbles: true });
            countrySelect.dispatchEvent(event);
        } else if (isSGLocale(locale) && countrySelect) {
            countrySelect.value = 'SGP';
            const event = new Event('change', { bubbles: true });
            countrySelect.dispatchEvent(event);
        }
    }, [locale, countriesAvail]);

    React.useEffect(() => {
        if (locale === 'it') {
            const stateSelect = document.getElementById('shipping_addressstate') as HTMLSelectElement;
            const optionDEF = document.querySelector('#shipping_addressstate option[value=DEF]') as HTMLOptionElement;

            if (stateSelect && optionDEF) {
                stateSelect.value = 'DEF';
                stateSelect.disabled = true;
                const event = new Event('change', { bubbles: true });
                stateSelect.dispatchEvent(event);
            }
        }
    }, [locale]);

    const validateFormData = React.useCallback(() => {
        const rawFormData = {
            Name: (document.getElementById('shipping_addressname') as HTMLInputElement)?.value || '',
            Street: (document.getElementById('shipping_addressstreet') as HTMLInputElement)?.value || '',
            City: (document.getElementById('shipping_addresscity') as HTMLInputElement)?.value || '',
            State: (document.getElementById('shipping_addressstate') as HTMLSelectElement)?.value || '',
            ZipCode: (document.getElementById('shipping_addresszipcode') as HTMLInputElement)?.value || '',
            CountyName: (document.getElementById('shipping_addresscounty') as HTMLInputElement)?.value?.toUpperCase() || '',
            ThreeLetterISORegionName:
                (document.getElementById('shipping_addressthreeletterisoregionname') as HTMLSelectElement)?.value || '',
            // Prefer the local phone input value (without country prefix) for validation
            Phone:
                (document.getElementById('shipping_addressphone') as HTMLInputElement)?.value ||
                fullPhoneNumber ||
                (document.getElementById('shipping_addressphone_full') as HTMLInputElement)?.value ||
                ''
        };

        const formData: any = {};
        Object.entries(rawFormData).forEach(([key, value]) => {
            if (value && value.trim() !== '') {
                formData[key] = value;
            }
        });

        const alwaysInclude = ['Name', 'Street', 'City', 'ZipCode', 'ThreeLetterISORegionName', 'Phone'];
        alwaysInclude.forEach(field => {
            if (!(field in formData)) {
                formData[field] = rawFormData[field as keyof typeof rawFormData] || '';
            }
        });

        if (isUSLocale(locale) && !('State' in formData)) {
            formData.State = rawFormData.State;
        }

        if (isITLocale(locale)) {
            formData.CountyName = (formData.CountyName || rawFormData.CountyName).toUpperCase();
        }

        if (isSGLocale(locale)) {
            if ('State' in formData && (formData.State === '' || formData.State === 'DEF')) {
                delete formData.State;
            }
            if ('CountyName' in formData && formData.CountyName === '') {
                delete formData.CountyName;
            }
        }

        if (!isITLocale(locale) && 'CountyName' in formData) {
            delete formData.CountyName;
        }

        if (!isUSLocale(locale) && 'State' in formData && (formData.State === '' || formData.State === 'DEF')) {
            delete formData.State;
        }

        const errors: { [key: string]: string } = {};

        try {
            addressSchema.parse(formData);
        } catch (error) {
            if (error instanceof z.ZodError) {
                error.errors.forEach(err => {
                    if (err.path.length > 0) {
                        errors[err.path[0] as string] = err.message;
                    }
                });
            }
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }, [addressSchema, locale]);

    const validateFormDataRef = React.useRef(validateFormData);
    React.useEffect(() => {
        validateFormDataRef.current = validateFormData;
    }, [validateFormData]);

    React.useEffect(() => {
        const saveButton = document.querySelector('.msc-address-form__button-save.msc-btn') as HTMLButtonElement;
        if (saveButton) {
            const handleSaveClick = (e: Event) => {
                try {
                    const phoneInput = document.getElementById('shipping_addressphone') as HTMLInputElement | null;
                    const hiddenPhoneInput = document.getElementById('shipping_addressphone_full') as HTMLInputElement | null;
                    const currentHidden = hiddenPhoneInput?.value || '';
                    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;

                    const prevVisible = phoneInput?.value || '';
                    const countrySelect = document.querySelector('.msc-address-form__phone-country-selector') as HTMLSelectElement | null;
                    const countryDialRaw = countrySelect?.value || '';
                    const countryDial = (countryDialRaw || '').replace(/\s+/g, '');
                    const localDigits = (prevVisible || '').replace(/[^\d]/g, '');
                    const countryDialNoPlus = countryDial.replace(/\+/g, '');
                    const computedFull = countryDial
                        ? `+${countryDialNoPlus}${localDigits}`
                        : fullPhoneNumber || currentHidden || localDigits;

                    if (hiddenPhoneInput) {
                        if (nativeInputValueSetter) {
                            nativeInputValueSetter.call(hiddenPhoneInput, computedFull);
                        } else {
                            hiddenPhoneInput.value = computedFull;
                        }
                    }
                } catch (error) {
                    console.error('[Account Address Validation] Phone field update error:', error);
                }

                const validation = validateFormDataRef.current();

                if (isITLocale(locale)) {
                    const countyField = document.getElementById('shipping_addresscounty') as HTMLInputElement;
                    countyField.setAttribute('value', countyField.value.toUpperCase());
                }

                // Log validation summary
                console.log('[Account Address Validation]', {
                    isValid: validation.isValid,
                    errors: validation.errors,
                    locale,
                    timestamp: new Date().toISOString()
                });

                if (!validation.isValid) {
                    e.preventDefault();
                    e.stopPropagation();

                    Object.keys(validation.errors).forEach(fieldName => {
                        const fieldId = `shipping_address${fieldName.toLowerCase()}`;
                        const field = document.getElementById(fieldId);
                        const container = field?.closest('.msc-address-form__item') as HTMLElement;

                        if (container) {
                            container.classList.add('address-form__item-invalid');
                        }
                    });
                } else {
                    showLoader();
                }
            };

            saveButton.addEventListener('click', handleSaveClick);
            return () => {
                saveButton.removeEventListener('click', handleSaveClick);
            };
        }
        return () => {};
    }, [locale, fullPhoneNumber]);

    const [hasAddressError, setHasAddressError] = React.useState(false);
    React.useEffect(() => {
        setHasAddressError(error.title ? true : false);
    }, [error.title]);

    return (
        <Node {...AddressForm}>
            {heading}
            {items &&
                items.map((item: IAddressAddItem) => {
                    const { AddressItem, key, label, input } = item;

                    const fieldError = validationErrors[key];
                    const hasValidationError = !!fieldError;

                    // Custom Phone field with country selector
                    if (key === 'Phone') {
                        return (
                            <Node
                                {...AddressItem}
                                key={key}
                                className={`${AddressItem.className} ${hasValidationError ? 'address-form__item-invalid' : ''}`}
                            >
                                {label}
                                <PhoneCountryCodeInput
                                    channelCountryRegionISOCode={phoneCountryCode}
                                    isCountryCodeHidden={false}
                                    phoneInputId='shipping_addressphone'
                                    phoneValue={fullPhoneNumber}
                                    initialCountryDialCode={initialCountryDialCode}
                                    useDefaultCountryCode={config?.useDefaultCountryCode}
                                    defaultCountryCode={config?.defaultCountryCode}
                                    onPhoneNumberChange={(phoneNum: string, fullPhone: string, hasError: boolean, selectedCountry?: CountryOption) => {
                                        // Update local state and hidden field
                                        handlePhoneNumberChange(phoneNum, fullPhone, hasError, selectedCountry);

                                        // If the original input element exists in the item, call its onChange
                                        try {
                                            const inputAny: any = input as any;
                                            const originalOnChange = inputAny?.props?.onChange;
                                            if (originalOnChange && typeof originalOnChange === 'function') {
                                                const originalInputName = inputAny?.props?.name || 'Phone';
                                                const syntheticEvent: any = { target: { name: originalInputName, value: fullPhone } };
                                                originalOnChange(syntheticEvent);
                                            }
                                        } catch (e) {
                                            // Swallow errors; we still have DOM writes as fallback
                                        }
                                    }}
                                    resources={{
                                        phoneValidationErrorMin: resources?.phoneValidationErrorMin,
                                        phoneValidationErrorMax: resources?.phoneValidationErrorMax,
                                        phoneValidationErrorNumeric: resources?.phoneValidationErrorNumeric,
                                        phoneValidationErrorRequired: resources?.phoneValidationErrorRequired
                                    }}
                                    className='msc-address-form__phone-container'
                                />
                                <CustomAlert message={fieldError || ''} isVisible={hasValidationError} />
                            </Node>
                        );
                    }

                    return (
                        <Node {...AddressItem} key={key}>
                            {label}
                            {input}
                            <CustomAlert message={fieldError || ''} isVisible={hasValidationError} />
                        </Node>
                    );
                })}

            {hasAddressError && (
                <Node {...error.AddressError}>
                    <p className='msc-address-form__error-message'>{resources?.addressErrorMessage || 'Address validation failed'}</p>
                </Node>
            )}

            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

const AccountManagementAddressView: React.FC<IAccountManagementAddressViewProps> = props => {
    const { hideLoader } = useGlobalLoader();
    const { AccountAddressManagement, viewState, showAddressList, showAddOrUpdateAddress } = props;

    React.useEffect(() => {
        hideLoader();
    }, [viewState.isShowAddresList, viewState.isShowAddOrUpdateAddress]);

    return (
        <Module {...AccountAddressManagement}>
            {viewState.isShowAddresList && <AddressLists {...showAddressList} />}
            {viewState.isShowAddOrUpdateAddress && <AddressAddUpdate {...showAddOrUpdateAddress} {...AccountAddressManagement} />}
        </Module>
    );
};

export default AccountManagementAddressView;
